import { TModal } from 'widgets/global/Modal';
import { RefElement, TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description Size Drawer — extends the existing Modal widget to create a
 * right-side slide-in panel for size selection on PDP.
 * Uses the exact same open/close pattern as MinicartDialog (g-backdrop_dialog
 * on container, g-slide_panel on dialog, toggled by m-opened/m-active classes
 * via Modal.showModal/closeModal).
 *
 * Implements the ProductVariantSelect interface (selectVariation, getSelectedVariations)
 * so the parent productDetail → ProductVariationChangeMixin handles AJAX updates.
 *
 * @param Modal Base widget for extending
 * @returns SizeDrawer widget
 */
function SizeDrawerClassCreator(Modal: TModal) {
    class SizeDrawer extends Modal {
        selectedVariation: null | TRefElementInstance = null;

        prefs() {
            return {
                // Modal uses refContainer to show/hide the backdrop.
                // We renamed the backdrop ref to 'backdrop' so that data-ref="container"
                // can be used exclusively for the size list (the re-render target).
                ...super.prefs(),
                refContainer: 'backdrop',
                classesSelected: 'm-selected',
                isComingSoon: false
            };
        }

        /**
         * @description Opens the drawer using Modal's showModal method
         */
        openDrawer() {
            this.showModal().then(() => {
                // After modal is open: if a size is already pre-selected and orderable
                // (e.g. one-size product), show ATC footer immediately.
                var selfEl = this.ref('self').get() as HTMLElement;

                if (!selfEl) {
                    return;
                }

                var selectedItem = selfEl.querySelector('[data-attr-is-selected="true"]');

                if (selectedItem && selectedItem.getAttribute('data-attr-is-coming-soon') === 'true') {
                    return;
                }

                if (selectedItem && selectedItem.getAttribute('data-attr-is-orderable') === 'true') {
                    var footer = selfEl.querySelector<HTMLElement>('.b-size_drawer-footer');

                    if (footer) {
                        footer.removeAttribute('hidden');
                    }
                }
            });
            document.querySelector('.b-back_to_top')?.classList.add('m-size_drawer_opened');
        }

        /**
         * @description Closes the drawer using Modal's closeModal method.
         * Always resets the ATC footer to hidden so the next open starts clean.
         */
        closeDrawer() {
            this.resetFooter();
            this.closeModal();
        }

        /**
         * @description Closes the drawer and removes the size drawer opened class from back to top element
         */
        close() {
            super.close();
            document.querySelector('.b-back_to_top')?.classList.remove('m-size_drawer_opened');
        }

        /**
         * @description Override cancel so X button / backdrop click also resets footer.
         */
        cancel() {
            this.resetFooter();
            super.cancel();
        }

        /**
         * @description Hides the ATC footer (resets state for next open).
         */
        resetFooter() {
            var selfEl = this.ref('self').get() as HTMLElement;

            if (selfEl) {
                var footer = selfEl.querySelector<HTMLElement>('.b-size_drawer-footer');

                if (footer) {
                    footer.setAttribute('hidden', 'hidden');
                }
            }
        }

        /**
         * @description Handles a size row click inside the drawer.
         * Highlights the selected row, shows the drawer ATC or Notify Me footer,
         * updates the trigger label, then emits 'change' for ProductVariationChangeMixin.
         * OOS items (data-attr-is-orderable="false") are selectable to trigger Notify Me.
         * @param el - clicked size item element
         */
        selectSize(el: TRefElementInstance) {
            // Clear selection from ALL items using direct DOM query.
            // this.ref('sizeItem') only targets the first matched element,
            // so we must iterate via querySelectorAll on the panel element.
            var panelEl = this.ref('dialog').get() as HTMLElement;

            if (panelEl) {
                var allItems = panelEl.querySelectorAll('[data-ref="sizeItem"]');

                for (var i = 0; i < allItems.length; i++) {
                    allItems[i].classList.remove('m-selected');
                    allItems[i].removeAttribute('aria-current');
                }
            }

            // Mark the clicked item as selected
            el.addClass(this.prefs().classesSelected);
            el.attr('aria-current', 'true');

            // Update the trigger label
            var displayValue = el.data('attrDisplayValue');

            if (displayValue) {
                var triggerEl = this.ref('triggerLabel').get();

                if (triggerEl) {
                    (triggerEl as HTMLElement).textContent = String(displayValue);
                }
            }

            var isOrderable = String(el.data('attrIsOrderable')) === 'true';
            var isComingSoon = String(el.data('attrIsComingSoon')) === 'true';

            if (isComingSoon) {
                // Coming soon: allow visual selection but do not show ATC footer.
                this.selectedVariation = el;
                this.emit('change', this);
                this.closeDrawer();

                return;
            }

            if (!isOrderable) {
                // OOS: close drawer immediately and emit change
                this.selectedVariation = el;
                this.emit('change', this);
                this.closeDrawer();

                return;
            }

            // In-stock: reveal the ATC footer. Query from widget root to bypass any ref-caching issues.
            var selfEl = this.ref('self').get() as HTMLElement;

            if (selfEl) {
                var footer = selfEl.querySelector<HTMLElement>('.b-size_drawer-footer');

                if (footer) {
                    footer.removeAttribute('hidden');
                }
            }

            // Save selected variation and emit change (ProductVariantSelect interface)
            this.selectedVariation = el;
            this.emit('change', this);
        }

        /**
         * @description Gets the selected variation element
         * (ProductVariantSelect interface for ProductVariationChangeMixin)
         * @returns selected variation or ref element of empty array
         */
        getSelectedVariations(): TRefElementInstance {
            if (this.selectedVariation) {
                return this.selectedVariation;
            }

            return new RefElement([]);
        }

        /**
         * @description selectVariation — compatibility alias
         * @param variation - selected variation
         */
        selectVariation(variation: TRefElementInstance) {
            this.selectedVariation = variation;
            this.emit('change', this);
        }

        onAfterCloseModal() {
            if (this.attributes) {
                Object.keys(this.attributes).forEach((key) => {
                    this.ref(this.prefs().refContainer).attr(key, false);
                });
            }
        }

        onBeforeShowModal(modalData: { attributes: Record<string, unknown> } | undefined) {
            if (modalData && modalData.attributes) {
                Object.keys(modalData.attributes).forEach((key) => {
                    const value = <string>modalData.attributes[key];

                    if (value === null || value === undefined || value === '') {
                        delete modalData.attributes[key];
                    }

                    this.ref(this.prefs().refContainer).attr(key, value);
                });
                this.attributes = modalData.attributes;
            }
        }

        /**
         * @description Handles add-to-cart click from within the drawer.
         * Closes drawer, then clicks the main PDP ATC button.
         */
        drawerAddToCart() {
            this.closeDrawer();

            var addToCartBtn = document.querySelector<HTMLElement>('[data-id="addToCart"]');

            if (addToCartBtn) {
                addToCartBtn.click();
            }
        }
    }

    return SizeDrawer;
}

export type TSizeDrawer = ReturnType<typeof SizeDrawerClassCreator>;

export type TSizeDrawerInstance = InstanceType<TSizeDrawer>;

export default SizeDrawerClassCreator;
